import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get('/api/users/profile');
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/users/login', {
        email,
        password
      });

      const { token: newToken, user: userData } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post('/api/users/register', userData);
      
      const { token: newToken, user: newUser } = response.data;
      
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(newUser);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  const updatePreferences = async (preferences) => {
    try {
      const response = await axios.put('/api/users/preferences', {
        preferences
      });
      
      setUser(prev => ({
        ...prev,
        preferences: response.data.preferences
      }));
      
      toast.success('Preferences updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update preferences';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const addToWatchlist = async (ticker) => {
    try {
      const response = await axios.post('/api/users/watchlist', { ticker });
      
      // Update user's watchlist in state
      setUser(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          watchlist: [
            ...prev.preferences.watchlist,
            { ticker: ticker.toUpperCase(), addedAt: new Date() }
          ]
        }
      }));
      
      toast.success(`${ticker.toUpperCase()} added to watchlist`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to add to watchlist';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const removeFromWatchlist = async (ticker) => {
    try {
      await axios.delete(`/api/users/watchlist/${ticker}`);
      
      // Update user's watchlist in state
      setUser(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          watchlist: prev.preferences.watchlist.filter(
            item => item.ticker.toLowerCase() !== ticker.toLowerCase()
          )
        }
      }));
      
      toast.success(`${ticker.toUpperCase()} removed from watchlist`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to remove from watchlist';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const isInWatchlist = (ticker) => {
    if (!user?.preferences?.watchlist) return false;
    return user.preferences.watchlist.some(
      item => item.ticker.toLowerCase() === ticker.toLowerCase()
    );
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    updatePreferences,
    addToWatchlist,
    removeFromWatchlist,
    isInWatchlist,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};